/**
 * ReportExecutionModal Component
 * Modal para configurar e executar relatório
 */

import React, { useState } from 'react';
import { Play, Download, Eye, SlidersHorizontal, BarChart3, FileDown } from 'lucide-react';
import type { ReportDefinition, ReportParameter } from '@/services/reports-service';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { FormField } from '@/components/common/FormSection';
import { cn } from '@/utils/cn';

interface ReportExecutionModalProps {
  report: ReportDefinition;
  isOpen: boolean;
  onClose: () => void;
  onExecute: (parameters: Record<string, unknown>, action: 'view' | 'export', format?: 'csv' | 'json') => void;
  isExecuting: boolean;
}

const SectionTitle: React.FC<{ icon: React.ComponentType<{ className?: string }>; children: React.ReactNode }> = ({
  icon: Icon,
  children
}) => (
  <h3 className="flex items-center gap-2 text-sm font-medium">
    <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
    {children}
  </h3>
);

const ParameterInput: React.FC<{
  param: ReportParameter;
  id: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ param, id, value, onChange }) => {
  if (param.type === 'select' && param.options) {
    return (
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Selecione...</option>
        {param.options.map(opt => (
          <option key={String(opt.value)} value={String(opt.value)}>
            {opt.label}
          </option>
        ))}
      </NativeSelect>
    );
  }

  return (
    <Input
      id={id}
      type={param.type === 'date' ? 'date' : 'text'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={param.type === 'date' ? undefined : param.description}
    />
  );
};

const ActionOption: React.FC<{
  value: 'view' | 'export';
  checked: boolean;
  onSelect: () => void;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  hint: string;
}> = ({ value, checked, onSelect, icon: Icon, title, hint }) => (
  <label
    className={cn(
      'flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50',
      checked && 'border-primary bg-primary/5'
    )}
  >
    <input
      type="radio"
      name="action"
      value={value}
      checked={checked}
      onChange={onSelect}
      className="size-4 accent-primary"
    />
    <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
    <div className="min-w-0">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  </label>
);

export const ReportExecutionModal: React.FC<ReportExecutionModalProps> = ({
  report,
  isOpen,
  onClose,
  onExecute,
  isExecuting
}) => {
  const [parameters, setParameters] = useState<Record<string, unknown>>({});
  const [action, setAction] = useState<'view' | 'export'>('view');
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');

  const handleExecute = () => {
    if (action === 'view') {
      onExecute(parameters, 'view');
    } else {
      onExecute(parameters, 'export', exportFormat);
    }
  };

  const handleParameterChange = (paramId: string, value: unknown) => {
    setParameters(prev => ({
      ...prev,
      [paramId]: value
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent size="lg" fullScreenOnMobile className="flex flex-col sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{report.name}</DialogTitle>
          <DialogDescription>{report.description}</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-y-auto">
          {report.parameters && report.parameters.length > 0 && (
            <section className="flex flex-col gap-3">
              <SectionTitle icon={SlidersHorizontal}>Parâmetros</SectionTitle>
              {report.parameters.map(param => {
                const fieldId = `report-param-${param.id}`;
                return (
                  <FormField
                    key={param.id}
                    htmlFor={fieldId}
                    label={<>{param.label}{param.required && <span className="text-destructive">*</span>}</>}
                    hint={param.description}
                  >
                    <ParameterInput
                      param={param}
                      id={fieldId}
                      value={String(parameters[param.id] ?? '')}
                      onChange={(value) => handleParameterChange(param.id, value)}
                    />
                  </FormField>
                );
              })}
            </section>
          )}

          <section className="flex flex-col gap-3">
            <SectionTitle icon={BarChart3}>Ação</SectionTitle>
            <div className="flex flex-col gap-2">
              <ActionOption
                value="view"
                checked={action === 'view'}
                onSelect={() => setAction('view')}
                icon={Eye}
                title="Visualizar na tela"
                hint="Ver dados e agregações"
              />
              <ActionOption
                value="export"
                checked={action === 'export'}
                onSelect={() => setAction('export')}
                icon={Download}
                title="Exportar arquivo"
                hint="Baixar CSV ou JSON"
              />
            </div>
          </section>

          {action === 'export' && (
            <section className="flex flex-col gap-3">
              <SectionTitle icon={FileDown}>Formato de exportação</SectionTitle>
              <div className="flex gap-2">
                {(['csv', 'json'] as const).map(format => (
                  <Button
                    key={format}
                    type="button"
                    variant={exportFormat === format ? 'secondary' : 'outline'}
                    aria-pressed={exportFormat === format}
                    onClick={() => setExportFormat(format)}
                    className={cn('flex-1', exportFormat === format && 'border-primary text-primary')}
                  >
                    {format.toUpperCase()} (.{format})
                  </Button>
                ))}
              </div>
            </section>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isExecuting}>
            Cancelar
          </Button>
          <Button onClick={handleExecute} loading={isExecuting}>
            {isExecuting ? (
              'Executando...'
            ) : (
              <>
                <Play className="size-4" />
                {action === 'view' ? 'Gerar Relatório' : 'Exportar'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
